/**
 * Size chart chip.
 *
 * Keeps the summary chip and the highlighted table row in step with whichever
 * size the shopper has selected. It reads strings the Liquid already rendered
 * onto each row - it never computes a measurement.
 *
 * Why delegation rather than binding to the radios: Savor re-renders the
 * variant picker through section rendering on every variant change, so any
 * listener attached to a radio is thrown away with the element that held it.
 * The previous build solved that with setInterval(tick, 1200) - a wake-up
 * every 1.2 seconds for the life of the page. A listener on the document
 * survives the re-render and costs nothing when nothing is happening.
 */
(function () {
  'use strict';

  var SELECTED = 'is-selected';

  /** @returns {HTMLDetailsElement|null} */
  function chart() {
    return document.querySelector('[data-tg-size-chart]');
  }

  /**
   * The sizes this product's chart actually knows about, keyed by upper-cased
   * label. Built fresh on each read so it survives a re-render of the panel.
   * A row is a table row on most charts and a button on the oversized tee
   * chart; both carry data-size, so match on that rather than on the markup.
   */
  function rowsBySize(root) {
    var map = Object.create(null);
    var rows = root.querySelectorAll('[data-tg-size-table] [data-size]');
    for (var i = 0; i < rows.length; i++) {
      var key = (rows[i].getAttribute('data-size') || '').trim().toUpperCase();
      if (key) map[key] = rows[i];
    }
    return map;
  }

  /**
   * The checked radio whose value matches a size this chart has a row for.
   * Matching against the chart rather than against a hard-coded size list is
   * what stopped the old build silently matching nothing on hoodies and pants.
   */
  function selectedSize(map) {
    var checked = document.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      var value = (checked[i].value || '').trim().toUpperCase();
      if (map[value]) return value;
    }
    return null;
  }

  var lastRendered = '';

  function render() {
    var root = chart();
    if (!root) return;

    var map = rowsBySize(root);
    var size = selectedSize(map);

    if (!size) {
      // A single-size product ("Basics - Beige ... - M") has no size radios at
      // all. There is nothing to select, so the chip can never fill in - but the
      // measurements are still the thing a shopper needs before buying. Show the
      // table without the chip rather than hiding the guide completely.
      var hasSizeInputs = false;
      var radios = document.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        if (map[(radios[i].value || '').trim().toUpperCase()]) {
          hasSizeInputs = true;
          break;
        }
      }

      if (hasSizeInputs) {
        // Sizes exist but none is chosen yet: the chip would be empty, so wait.
        root.hidden = true;
      } else {
        root.hidden = false;
        var heroEl = root.querySelector('[data-tg-chip-hero]');
        var restEl = root.querySelector('[data-tg-chip-rest]');
        if (heroEl) heroEl.textContent = 'Size guide';
        if (restEl) restEl.textContent = '';
      }

      lastRendered = '';
      return;
    }

    root.hidden = false;
    if (size === lastRendered) return;
    lastRendered = size;

    var row = map[size];
    var hero = root.querySelector('[data-tg-chip-hero]');
    var rest = root.querySelector('[data-tg-chip-rest]');

    // The row's data- attributes were escaped by Liquid and hold only markup
    // this theme generated, so assigning innerHTML here introduces nothing the
    // template did not already emit.
    if (hero) hero.innerHTML = row.getAttribute('data-chip-hero') || '';
    if (rest) rest.innerHTML = row.getAttribute('data-chip-rest') || '';

    for (var key in map) {
      map[key].classList.toggle(SELECTED, map[key] === row);
    }
  }

  // A variant change re-renders the picker, so read after the swap rather than
  // during it. One frame is enough; the old build waited 60ms and then polled.
  function scheduleRender() {
    requestAnimationFrame(render);
  }

  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (target instanceof HTMLInputElement && target.type === 'radio') scheduleRender();
    },
    true
  );

  // Savor's own variant events, for the case where the picker updates without
  // a radio change event reaching the document (quick add, swatch pickers).
  ['variant:update', 'product:selected', 'variantUpdate'].forEach(function (name) {
    document.addEventListener(name, scheduleRender);
  });

  // Oversized tee chart: tapping a size opens that size's tee measurements
  // and closes any other; tapping it again closes it. Delegated, like the
  // listeners above, because the chart is re-rendered with the picker.
  document.addEventListener('click', function (event) {
    var step = event.target instanceof Element ? event.target.closest('[data-tg-fit-step]') : null;
    if (!step) return;

    var table = step.closest('[data-tg-size-table]');
    var size = step.getAttribute('data-size');
    var open = step.getAttribute('aria-expanded') !== 'true';

    var steps = table.querySelectorAll('[data-tg-fit-step]');
    for (var i = 0; i < steps.length; i++) {
      steps[i].setAttribute('aria-expanded', String(open && steps[i] === step));
    }
    var cards = table.querySelectorAll('[data-tee-for]');
    for (var j = 0; j < cards.length; j++) {
      cards[j].hidden = !(open && cards[j].getAttribute('data-tee-for') === size);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
})();
